import i18n from 'i18n'
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { expensesAPI } from 'api/expenses'
import { isOfflineQueuedFulfilledAction } from 'helpers/offlineAction'
import { branchChanged } from './branchSlice'

interface ExpenseCategory {
 id: number
 name: string
 description?: string
 branch_id?: string
 expense_account_id?: number
 is_active: boolean
}

interface ExpenseAttachment {
 id: number
 file_path: string
 file_type: string
 original_name: string
 size: number
}

interface Expense {
 id: number
 expense_category_id: number
 branch_id?: string
 user_id: number
 vault_id?: string
 status?: string
 amount: number
 description?: string
 expense_date: string
 reference_number?: string
 notes?: string
 category?: ExpenseCategory
 branch?: { id: string; name: string }
 user?: { id: number; name: string }
 updated_by?: number | null
 updatedBy?: { id: number; name: string }
 vault?: { id: string; name: string }
 attachments?: ExpenseAttachment[]
 created_at: string
}

interface RecurringExpense {
 id: string
 branch_id: string
 vault_id?: string
 expense_category_id: number
 user_id: number
 title: string
 description?: string
 amount: number
 frequency: string
 start_date: string
 end_date?: string
 next_date?: string
 payment_method?: string
 is_active: boolean
}

interface ExpensesState {
 data: Expense[]
 categories: ExpenseCategory[]
 recurring: RecurringExpense[]
 summary: { total: number; count: number }
 loading: boolean
 error: string | null
 activeRequestId?: string
 hasServerSnapshot: boolean
 pagination: { total: number; per_page: number; current_page: number; last_page: number }
}

const initialState: ExpensesState = {
 data: [],
 categories: [],
 recurring: [],
 summary: { total: 0, count: 0 },
 loading: false,
 error: null,
 hasServerSnapshot: false,
 pagination: { total: 0, per_page: 20, current_page: 1, last_page: 1 },
}

export const fetchExpenses = createAsyncThunk(
 'expenses/fetchExpenses',
 async (params: any, { rejectWithValue }) => {
 try {
 const response = await expensesAPI.getAll(params)
 return response.data
 } catch (error: any) {
 return rejectWithValue(error.response?.data?.message || i18n.t('errors.expenses.fetchFailed'))
 }
 }
)

export const createExpense = createAsyncThunk(
 'expenses/createExpense',
 async (data: any, { rejectWithValue }) => {
 try {
 const response = await expensesAPI.create(data)
 return response.data
 } catch (error: any) {
 return rejectWithValue(error.response?.data?.message || i18n.t('errors.expenses.createFailed'))
 }
 }
)

export const cancelExpense = createAsyncThunk(
 'expenses/cancelExpense',
 async (id: number, { rejectWithValue }) => {
 try {
 await expensesAPI.cancel(id)
 return id
 } catch (error: any) {
 return rejectWithValue(error.response?.data?.message || i18n.t('errors.expenses.cancelFailed'))
 }
 }
)

export const fetchExpenseCategories = createAsyncThunk(
 'expenses/fetchCategories',
 async (params: any, { rejectWithValue }) => {
 try {
 const response = await expensesAPI.getCategories(params)
 return response.data?.data ?? response.data ?? []
 } catch (error: any) {
 return rejectWithValue(error.response?.data?.message || i18n.t('errors.expenses.fetchCategoriesFailed'))
 }
 }
)

export const createExpenseCategory = createAsyncThunk(
 'expenses/createCategory',
 async (data: any, { rejectWithValue }) => {
 try {
 const response = await expensesAPI.createCategory(data)
 return response.data?.data ?? response.data
 } catch (error: any) {
 return rejectWithValue(error.response?.data?.message || i18n.t('errors.expenses.createCategoryFailed'))
 }
 }
)

export const updateExpenseCategory = createAsyncThunk(
 'expenses/updateCategory',
 async ({ id, data }: { id: number; data: any }, { rejectWithValue }) => {
 try {
 const response = await expensesAPI.updateCategory(id, data)
 return response.data?.data ?? response.data
 } catch (error: any) {
 return rejectWithValue(error.response?.data?.message || i18n.t('errors.expenses.updateCategoryFailed'))
 }
 }
)

export const deleteExpenseCategory = createAsyncThunk(
 'expenses/deleteCategory',
 async (id: number, { rejectWithValue }) => {
 try {
 await expensesAPI.deleteCategory(id)
 return id
 } catch (error: any) {
 return rejectWithValue(error.response?.data?.message || i18n.t('errors.expenses.deleteCategoryFailed'))
 }
 }
)

export const fetchRecurringExpenses = createAsyncThunk(
 'expenses/fetchRecurring',
 async (params: any, { rejectWithValue }) => {
 try {
 const response = await expensesAPI.getRecurring(params)
 return response.data
 } catch (error: any) {
 return rejectWithValue(error.response?.data?.message || i18n.t('errors.expenses.fetchRecurringFailed'))
 }
 }
)

export const createRecurringExpense = createAsyncThunk(
 'expenses/createRecurring',
 async (data: any, { rejectWithValue }) => {
 try {
 const response = await expensesAPI.createRecurring(data)
 return response.data
 } catch (error: any) {
 return rejectWithValue(error.response?.data?.message || i18n.t('errors.expenses.createRecurringFailed'))
 }
 }
)

const expensesSlice = createSlice({
 name: 'expenses',
 initialState,
 reducers: {
 clearExpenses: (state) => {
 state.data = []
 state.summary = { total: 0, count: 0 }
 state.error = null
 state.activeRequestId = undefined
 },
 },
 extraReducers: (builder) => {
 builder
 .addCase(fetchExpenses.pending, (state, action) => {
 state.loading = true
 state.error = null
 state.activeRequestId = action.meta.requestId
 })
 .addCase(fetchExpenses.fulfilled, (state, action) => {
 const isOfflineCacheHydration = action.meta.requestId === 'offline-cache-hydrate'
 if (isOfflineCacheHydration && state.hasServerSnapshot) return
 if (!isOfflineCacheHydration && state.activeRequestId !== action.meta.requestId) return
 state.loading = false
 state.data = Array.isArray(action.payload?.expenses) ? action.payload.expenses : []
 state.summary = action.payload?.summary ?? { total: 0, count: 0 }
 state.pagination = action.payload?.pagination ?? initialState.pagination
 if (!isOfflineCacheHydration) {
 state.hasServerSnapshot = true
 state.activeRequestId = undefined
 }
 })
 .addCase(fetchExpenses.rejected, (state, action) => {
 if (state.activeRequestId !== action.meta.requestId) return
 state.loading = false
 state.error = action.payload as string
 state.activeRequestId = undefined
 })
 .addCase(createExpense.fulfilled, (state, action) => {
 if (isOfflineQueuedFulfilledAction(action)) return

 const created = action.payload as Expense | undefined
 if (!created?.id) return

 const existingIndex = state.data.findIndex((expense) => expense.id === created.id)
 if (existingIndex >= 0) {
 state.data[existingIndex] = created
 return
 }

 if (state.pagination.current_page === 1) {
 state.data.unshift(created)
 state.data = state.data.slice(0, state.pagination.per_page)
 }
 state.pagination.total += 1
 if (created.status !== 'cancelled') {
 state.summary.total += Number(created.amount || 0)
 state.summary.count += 1
 }
 })
 .addCase(cancelExpense.fulfilled, (state, action) => {
 const index = state.data.findIndex((expense) => expense.id === action.payload)
 const expense = index >= 0 ? state.data[index] : null
 if (expense && expense.status !== 'cancelled') {
 state.data[index] = { ...expense, status: 'cancelled' }
 state.summary.total = Math.max(0, state.summary.total - Number(expense.amount || 0))
 state.summary.count = Math.max(0, state.summary.count - 1)
 }
 })
 .addCase(fetchExpenseCategories.fulfilled, (state, action) => { state.categories = action.payload })
 .addCase(createExpenseCategory.fulfilled, (state, action) => {
 if (isOfflineQueuedFulfilledAction(action)) {
 return
 }
 if (action.payload) state.categories.push(action.payload)
 })
 .addCase(updateExpenseCategory.fulfilled, (state, action) => {
 const idx = state.categories.findIndex((c) => c.id === action.payload?.id)
 if (idx !== -1 && action.payload) state.categories[idx] = action.payload
 })
 .addCase(deleteExpenseCategory.fulfilled, (state, action) => {
 state.categories = state.categories.filter((c) => c.id !== action.payload)
 })
 .addCase(fetchRecurringExpenses.fulfilled, (state, action) => {
 state.recurring = action.payload.data ?? action.payload
 })
 .addCase(createRecurringExpense.fulfilled, (state, action) => {
 if (isOfflineQueuedFulfilledAction(action)) {
 return
 }
 state.recurring.unshift(action.payload)
 })
 .addCase(branchChanged, (state) => {
 Object.assign(state, initialState)
 })
 },
})

export const { clearExpenses } = expensesSlice.actions
export default expensesSlice.reducer
